import { useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from './api.js';
import TemplateRenderer from './renderers.jsx';
import AppRenderer from './AppRenderer.jsx';

// Player runtime — the screen the user actually sees on the TV.
//
// Loop architecture:
//   * Heartbeat every HEARTBEAT_MS (~30s). The response includes a
//     playbackHash; when it changes we refetch /me/playback.
//   * On boot, do an immediate heartbeat AND playback fetch in parallel so
//     the screen shows content within the first second.
//   * Every fetched playback is mirrored to localStorage so a reload (or a
//     cold boot with no network) shows last-known content immediately while
//     we try to fetch fresh.
//
// 401 from any call → bubble up to App which clears the token and falls back
// to the pairing screen.

const HEARTBEAT_MS    = 30_000;
const PLAYBACK_CACHE  = 'player:lastPlayback';
const APP_VERSION     = import.meta.env?.VITE_APP_VERSION || 'dev';

// Phase 7.4 — OTA version check. Bumped on each player-web release.
// When GET /player/version returns a higher versionCode, the banner
// surfaces "Update available" with a link to the APK download.
const CURRENT_VERSION_CODE = Number(import.meta.env?.VITE_PLAYER_VERSION_CODE || 1);
const VERSION_POLL_MS = 60 * 60 * 1000;  // hourly

export default function Player({ onUnauthorized }) {
  const [playback, setPlayback] = useState(() => loadCachedPlayback());
  const [online, setOnline]     = useState(true);
  const [upgrade, setUpgrade]   = useState(null);  // { versionName, apkUrl }
  const playbackHashRef         = useRef(null);

  // Hourly OTA poll. Silent on failure (network drop, endpoint down) —
  // we don't want a transient blip turning into a permanent banner.
  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const r = await apiGet('/player/version');
        if (cancelled) return;
        const nextCode = Number(r?.versionCode);
        if (Number.isFinite(nextCode) && nextCode > CURRENT_VERSION_CODE) {
          setUpgrade({
            versionName: r.versionName || `v${nextCode}`,
            apkUrl: r.apkUrl || null,
          });
        }
      } catch { /* silent */ }
    }
    check();
    const handle = setInterval(check, VERSION_POLL_MS);
    return () => { cancelled = true; clearInterval(handle); };
  }, []);

  // Heartbeat + playback poll loop.
  useEffect(() => {
    let stopped = false;

    async function tick() {
      try {
        const hb = await apiPost('/screens/me/heartbeat', {
          userAgent: navigator.userAgent,
          appVersion: APP_VERSION,
        });
        setOnline(true);

        if (hb.playbackHash !== playbackHashRef.current) {
          const pb = await apiGet('/screens/me/playback');
          if (stopped) return;
          playbackHashRef.current = hb.playbackHash;
          setPlayback(pb);
          saveCachedPlayback(pb);
        }
      } catch (err) {
        if (err.status === 401) { onUnauthorized?.(); return; }
        setOnline(false);
        // eslint-disable-next-line no-console
        console.warn('[player] heartbeat failed:', err.message);
      }
    }

    tick();                               // immediate first beat
    const handle = setInterval(tick, HEARTBEAT_MS);
    return () => { stopped = true; clearInterval(handle); };
  }, [onUnauthorized]);

  if (playback?.offair) {
    // Phase 7.3 — playlist is outside its dayparts window. Show a
    // calm "resumes at X" placeholder rather than rotating items.
    const nextAt = playback.nextWindowAt;
    const resumeStr = nextAt ? new Date(nextAt).toLocaleString(undefined, {
      weekday: 'short', hour: 'numeric', minute: '2-digit',
    }) : 'later today';
    return (
      <>
        <IdleScreen
          screenName={playback?.screen?.name}
          message={`Scheduled to resume ${resumeStr}.`}
        />
        <StatusOverlays online={online} upgrade={upgrade} />
      </>
    );
  }

  if (!playback || (!playback.template && !playback.playlist)) {
    return (
      <>
        <IdleScreen screenName={playback?.screen?.name} />
        <StatusOverlays online={online} upgrade={upgrade} />
      </>
    );
  }

  if (playback.template) {
    // animationKey ties to the template id — when the content changes (a
    // new push or a playlist advance) entrance animations replay because
    // the wrapped <CanvasElement key={id-animKey}> remounts.
    return (
      <>
        <TemplateRenderer template={playback.template}
          animationKey={playback.template?.id || playback.updatedAt} />
        <StatusOverlays online={online} upgrade={upgrade} />
      </>
    );
  }

  return (
    <>
      <PlaylistRunner playlist={playback.playlist} />
      <StatusOverlays online={online} upgrade={upgrade} />
    </>
  );
}

// ─── Playlist runner ──────────────────────────────────────────────────────
function PlaylistRunner({ playlist }) {
  const [idx, setIdx] = useState(0);
  const items = playlist.items || [];

  useEffect(() => {
    if (!items.length) return;
    const current = items[idx % items.length];
    const ms = Math.max(1, Number(current.durationSec || 10)) * 1000;
    const handle = setTimeout(() => setIdx((i) => i + 1), ms);
    return () => clearTimeout(handle);
  }, [idx, items]);

  if (!items.length) {
    return <IdleScreen screenName={playlist.name} message="This playlist is empty." />;
  }

  const current = items[idx % items.length];
  // animationKey bumps on every playlist advance so each new slide replays
  // its entrance animations.
  // Phase 10.8 — items are either template OR app. The backend response
  // sets exactly one of those keys. Branch render path here so a bug
  // in app rendering can never break template playback.
  if (current.app) {
    // Use the item id as the React key so a new app instance mounts
    // fresh on every advance (resets iframes, slideshow indexes, etc).
    return <AppRenderer key={current.id} app={current.app} />;
  }
  return <TemplateRenderer template={current.template} animationKey={idx} />;
}

// ─── Status overlays ──────────────────────────────────────────────────────
function IdleScreen({ screenName, message }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: '#08061a', color: '#9ca3af',
      textAlign: 'center', padding: '4vh 4vw',
    }}>
      <span style={{
        fontFamily: "'Space Mono', monospace",
        fontSize: 14, color: '#7c3aed', letterSpacing: 4, marginBottom: 24,
      }}>▶ OPSORA SCREENS</span>
      <h1 style={{ fontSize: 'clamp(28px, 4vw, 56px)', fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>
        {screenName || 'This screen'} is paired
      </h1>
      <p style={{ fontSize: 'clamp(14px, 1.4vw, 20px)', maxWidth: '60ch' }}>
        {message || 'Waiting for content. Push a template or playlist from the manager dashboard.'}
      </p>
    </div>
  );
}

function OfflineBadge() {
  return (
    <div style={{
      position: 'absolute', top: 16, right: 16,
      background: 'rgba(248, 113, 113, 0.15)',
      border: '1px solid rgba(248, 113, 113, 0.4)',
      borderRadius: 8, padding: '6px 12px',
      fontSize: 12, color: '#fb7185',
      fontFamily: "'Space Mono', monospace", letterSpacing: 1,
    }}>
      OFFLINE
    </div>
  );
}

// Phase 7.4 — overlays cluster. Renders the offline badge AND the
// update-available banner (when GET /player/version reports a newer
// versionCode). The banner sits at the top, the offline pill at the
// top-right; they don't visually collide.
function StatusOverlays({ online, upgrade }) {
  return (
    <>
      {upgrade && <UpgradeBanner upgrade={upgrade} />}
      {!online && <OfflineBadge />}
    </>
  );
}

function UpgradeBanner({ upgrade }) {
  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0,
      background: 'rgba(124, 58, 237, 0.92)',
      color: '#fff', padding: '8px 16px',
      fontSize: 13, fontFamily: "'Space Mono', monospace",
      letterSpacing: 0.5, textAlign: 'center',
      borderBottom: '1px solid rgba(255,255,255,0.2)',
      zIndex: 50,
    }}>
      UPDATE AVAILABLE · {upgrade.versionName} — restart this player to apply
      {upgrade.apkUrl && (
        <>
          {' '}·{' '}
          <a href={upgrade.apkUrl} style={{ color: '#fff', textDecoration: 'underline' }}>
            download APK
          </a>
        </>
      )}
    </div>
  );
}

// ─── Cached playback (offline survival) ───────────────────────────────────
function loadCachedPlayback() {
  try {
    const raw = localStorage.getItem(PLAYBACK_CACHE);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveCachedPlayback(pb) {
  try { localStorage.setItem(PLAYBACK_CACHE, JSON.stringify(pb)); } catch { /* quota */ }
}
